import { unityBridge } from './unity-bridge';
import { googleAuthService } from './google-auth-service';
import { googleSheetsService } from './google-sheets-service';
import { dynamicSheetManager, type SheetInfo, type TableData } from './services/dynamic-sheet-manager';

class LocalizationSyncService {
  private isSyncing = false;
  private syncCompleteCallbacks: (() => void)[] = [];

  /**
   * Initialize the sync service and expose global functions
   */
  initialize(): void {
    // Expose function for Unity to call
    (window as any).requestLocalizationSync = this.startSync.bind(this);
    (window as any).onLocalizationSyncComplete = this.onSyncComplete.bind(this);

    console.log('[LOCALIZATION SYNC] Service initialized');
  }

  /**
   * Start the localization sync process
   */
  async startSync(): Promise<void> {
    if (this.isSyncing) {
      console.warn('[LOCALIZATION SYNC] Sync already in progress');
      return;
    }

    console.log('[LOCALIZATION SYNC] Starting sync process');
    this.isSyncing = true;

    try {
      // Step 0: Wait for Unity Bridge to be initialized (CRITICAL: prevents race condition)
      // Use longer timeout (60 seconds) to account for slow page loads
      console.log('[LOCALIZATION SYNC] Waiting for Unity bridge initialization...');
      await unityBridge.waitForInitialization(60000); // 60 second timeout
      console.log('[LOCALIZATION SYNC] Unity bridge ready, proceeding with sync');

      // Step 1: Update status
      unityBridge.updateSyncStatus('Connecting to Google...');

      // Step 2: Ensure Google Auth is ready
      await this.ensureGoogleAuth();

      // Step 3: Fetch data from Google Sheets
      unityBridge.updateSyncStatus('Fetching latest content...');
      const entries = await this.fetchGoogleSheetsData();

      // Step 4: Validate that we have data before sending to Unity
      if (entries.length === 0) {
        console.error('[LOCALIZATION SYNC] No entries fetched from Google Sheets - auth may have failed or sheets are empty');
        throw new Error('No localization data found. Please verify Google Sheets access and sheet configuration.');
      }

      // Step 5: Send to Unity
      unityBridge.updateSyncStatus(`Loading ${entries.length} entries from ${this.getUniqueSheetCount(entries)} sheets...`);
      unityBridge.sendLocalizationSync(entries.map(entry => ({ key: entry.key, text: entry.text })));

      console.log(`[LOCALIZATION SYNC] Sync data sent to Unity: ${entries.length} entries from ${this.getUniqueSheetCount(entries)} sheets`);
    } catch (error) {
      console.error('[LOCALIZATION SYNC] Sync failed:', error);

      // CRITICAL: Don't send empty data to Unity on failure
      // Instead, show error and let Unity hide the loading screen
      unityBridge.updateSyncStatus(`Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);

      // Wait a bit then hide the loading screen
      setTimeout(() => {
        this.isSyncing = false;
        // Notify Unity to hide loading screen on failure
        (window as any).onLocalizationSyncComplete?.();
      }, 3000); // Show error for 3 seconds
    }
  }

  /**
   * Ensure Google Auth is ready - waits indefinitely for user to sign in
   * NOTE: This assumes Unity bridge is already initialized (called after waitForInitialization)
   */
  private async ensureGoogleAuth(): Promise<void> {
    return new Promise((resolve) => {
      // Check if already authorized
      const token = googleAuthService.getAccessToken();
      if (token) {
        googleSheetsService.setAccessToken(token);
        console.log('[LOCALIZATION SYNC] Already authenticated, proceeding with sync');
        resolve();
        return;
      }

      // Wait for auth to be ready - no timeout, wait as long as needed
      // Unity bridge must be initialized before this point
      unityBridge.updateSyncStatus('Waiting for Google sign-in...');
      console.log('[LOCALIZATION SYNC] Waiting for user to complete Google sign-in...');

      const checkAuth = setInterval(() => {
        const currentToken = googleAuthService.getAccessToken();
        if (currentToken) {
          clearInterval(checkAuth);
          googleSheetsService.setAccessToken(currentToken);
          console.log('[LOCALIZATION SYNC] Authentication successful after waiting, token acquired');
          resolve();
        }
        // No timeout - keep checking until user signs in
      }, 500);

      // Try to trigger sign-in after a brief delay
      setTimeout(() => {
        if (!googleAuthService.getAccessToken()) {
          unityBridge.updateSyncStatus('Please sign in with Google to continue...');
          googleAuthService.signIn().catch(error => {
            console.error('[LOCALIZATION SYNC] Sign-in prompt failed:', error);
            // Even if sign-in fails initially, keep waiting for user to manually sign in
            // Don't reject here - the user might complete sign-in from the UI
          });
        }
      }, 2000);
    });
  }

  /**
   * Fetch localization data from Google Sheets using dynamic sheet discovery
   */
  private async fetchGoogleSheetsData(): Promise<Array<{ key: string; text: string; source?: string }>> {
    const allEntries: Array<{ key: string; text: string; source?: string }> = [];

    try {
      console.log('[LOCALIZATION SYNC] Discovering all existing sheets...');

      // Use dynamic sheet manager to discover all existing sheets
      const discoveredSheets = await dynamicSheetManager.getAllTables();

      if (discoveredSheets.length === 0) {
        console.error('[LOCALIZATION SYNC] No sheets discovered in the master spreadsheet - this likely indicates an auth or configuration error');
        throw new Error('No Google Sheets found. Please verify authentication and sheet configuration in app-config.ts');
      }

      console.log(`[LOCALIZATION SYNC] Found ${discoveredSheets.length} sheets to sync from`);

      // Fetch data from all discovered sheets in parallel
      const fetchPromises = discoveredSheets.map(async (sheetInfo) => {
        try {
          console.log(`[LOCALIZATION SYNC] Fetching data from sheet: ${sheetInfo.sheetName} (table: ${sheetInfo.tableName})`);

          const tableData = await dynamicSheetManager.getTableDataFromSheet(sheetInfo.tableName);

          if (!tableData || tableData.rows.length === 0) {
            console.warn(`[LOCALIZATION SYNC] No data found in sheet: ${sheetInfo.sheetName}`);
            return [];
          }

          return this.parseTableData(tableData, sheetInfo);
        } catch (error) {
          console.error(`[LOCALIZATION SYNC] Failed to fetch from sheet ${sheetInfo.sheetName}:`, error);
          return []; // Continue with other sheets even if one fails
        }
      });

      // Wait for all sheet fetches to complete
      const sheetResults = await Promise.all(fetchPromises);

      // Merge all results
      sheetResults.forEach((sheetEntries, index) => {
        const sheetInfo = discoveredSheets[index];
        console.log(`[LOCALIZATION SYNC] Sheet "${sheetInfo.sheetName}": ${sheetEntries.length} entries`);
        allEntries.push(...sheetEntries);
      });

      console.log(`[LOCALIZATION SYNC] Total entries from all sheets: ${allEntries.length}`);

    } catch (error) {
      console.error('[LOCALIZATION SYNC] Failed to fetch from Google Sheets:', error);
      throw error;
    }

    return allEntries;
  }

  /**
   * Parse table data from dynamic sheet manager into localization entries
   */
  private parseTableData(tableData: TableData, sheetInfo: SheetInfo): Array<{ key: string; text: string; source?: string }> {
    const entries: Array<{ key: string; text: string; source?: string }> = [];

    if (!tableData || !tableData.rows || tableData.rows.length === 0) {
      return entries;
    }

      // Prefer the configured primary language column (or fall back to the first available language)
    let textColumnIndex = sheetInfo.languageColumns.get('english');
    if (textColumnIndex === undefined) {
      // Try other common language names
      const availableLanguages = Array.from(sheetInfo.languageColumns.keys());
      if (availableLanguages.length > 0) {
        textColumnIndex = sheetInfo.languageColumns.get(availableLanguages[0]);
        console.log(`[LOCALIZATION SYNC] Using language "${availableLanguages[0]}" for sheet: ${sheetInfo.sheetName}`);
      }
    }

    if (textColumnIndex === undefined) {
      console.warn(`[LOCALIZATION SYNC] No language column found in sheet: ${sheetInfo.sheetName}`);
      return entries;
    }

    // Process each row
    for (const row of tableData.rows) {
      if (row && row[sheetInfo.keyColumnIndex]) {
        const key = row[sheetInfo.keyColumnIndex].toString().trim();
        const text = row[textColumnIndex] ? row[textColumnIndex].toString() : '';

        // Only add entries that have valid keys and content
        if (key && (key.includes('#') || text)) {
          entries.push({
            key,
            text,
            source: sheetInfo.sheetName
          });
        }
      }
    }

    return entries;
  }


  /**
   * Get count of unique sheets from entries
   */
  private getUniqueSheetCount(entries: Array<{ key: string; text: string; source?: string }>): number {
    const uniqueSources = new Set(entries.map(entry => entry.source || 'Unknown').filter(Boolean));
    return uniqueSources.size;
  }

  /**
   * Called when Unity completes the sync
   */
  private onSyncComplete(): void {
    console.log('[LOCALIZATION SYNC] Unity reports sync complete');
    this.isSyncing = false;

    // Notify any callbacks
    this.syncCompleteCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[LOCALIZATION SYNC] Error in sync complete callback:', error);
      }
    });
  }

  /**
   * Register a callback for when sync completes
   */
  onSyncCompleted(callback: () => void): void {
    this.syncCompleteCallbacks.push(callback);
  }

  /**
   * Check if sync is in progress
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }
}

// Export singleton instance
export const localizationSyncService = new LocalizationSyncService();

// Auto-initialize when loaded
localizationSyncService.initialize();
